/**
 * Las etiquetas en español de los tipos del dominio.
 *
 * El código habla inglés; la pantalla, español. Este archivo es la frontera, y
 * es el único lugar donde se traduce. Un `PAID` en el markup de una vista es un
 * bug de esta capa.
 *
 * `Consultorio` **no** está aquí: es configurable por organización
 * (`organizations.unit_label`) porque el negocio no está limitado al sector
 * médico (§5).
 */

import type {
  AllocationMode, AppRole, BillingFrequency, ChargeStatus, DiscountType, IncidentSeverity,
  IncidentStatus, InvoiceStatus, ObligationCategory, ObligationStatus, PaymentMethod,
  PaymentStatus, Provenance, RequestCategory, RequestStatus, SubmissionStatus,
  SubscriptionStatus, TxnDirection, UnitStatus, UnitType, VisitKind, VisitStatus,
} from './enums.js';
import type { BreakKind } from './schedule.js';

/** El tono visual de una etiqueta de estado. */
export type Tone = 'ok' | 'warn' | 'danger' | 'info' | 'neutral';

export const APP_ROLE_LABEL: Record<AppRole, string> = {
  ADMIN: 'Administrador',
  CLIENT_ADMIN: 'Administrador de consultorio',
  CLIENT_MEMBER: 'Cliente',
  EMPLOYEE: 'Personal de limpieza',
};

export const UNIT_STATUS_LABEL: Record<UnitStatus, string> = {
  PROSPECT: 'Prospecto',
  ACTIVE: 'Activo',
  PAUSED: 'En pausa',
  ENDED: 'Finalizado',
};

export const UNIT_STATUS_TONE: Record<UnitStatus, Tone> = {
  PROSPECT: 'info',
  ACTIVE: 'ok',
  PAUSED: 'warn',
  ENDED: 'neutral',
};

export const UNIT_TYPE_LABEL: Record<UnitType, string> = {
  CLINIC: 'Consultorio',
  OFFICE: 'Oficina',
  BUILDING: 'Edificio',
  STORE: 'Local comercial',
  COMPLEX: 'Complejo',
  OTHER: 'Otro',
};

export const SUBSCRIPTION_STATUS_LABEL: Record<SubscriptionStatus, string> = {
  DRAFT: 'Borrador',
  ACTIVE: 'Activo',
  PAUSED: 'En pausa',
  ENDED: 'Finalizado',
  CANCELLED: 'Cancelado',
};

export const SUBSCRIPTION_STATUS_TONE: Record<SubscriptionStatus, Tone> = {
  DRAFT: 'neutral',
  ACTIVE: 'ok',
  PAUSED: 'warn',
  ENDED: 'neutral',
  CANCELLED: 'danger',
};

export const ALLOCATION_MODE_LABEL: Record<AllocationMode, string> = {
  SPLIT: 'Reparto',
  ROTATION: 'Rotación',
};

/** La explicación corta que va bajo el selector, para que nadie elija a ciegas. */
export const ALLOCATION_MODE_HINT: Record<AllocationMode, string> = {
  SPLIT: 'Uno o más pagadores se reparten el cobro cada mes: por igual, por porcentaje o por monto fijo.',
  ROTATION: 'Un pagador distinto cubre el cobro completo cada mes, por turnos.',
};

export const DISCOUNT_TYPE_LABEL: Record<DiscountType, string> = {
  NONE: 'Sin descuento',
  FIXED: 'Monto fijo',
  PERCENTAGE: 'Porcentaje',
};

export const CHARGE_STATUS_LABEL: Record<ChargeStatus, string> = {
  DRAFT: 'Borrador',
  ISSUED: 'Emitido',
  VOID: 'Anulado',
};

export const CHARGE_STATUS_TONE: Record<ChargeStatus, Tone> = {
  DRAFT: 'neutral',
  ISSUED: 'info',
  VOID: 'danger',
};

export const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  PENDING: 'Pendiente de pago',
  UNDER_REVIEW: 'En revisión',
  PARTIALLY_PAID: 'Pago parcial',
  PAID: 'Pagado',
  FAILED: 'Fallido',
  REFUNDED: 'Reintegrado',
};

export const PAYMENT_STATUS_TONE: Record<PaymentStatus, Tone> = {
  PENDING: 'warn',
  UNDER_REVIEW: 'info',
  PARTIALLY_PAID: 'warn',
  PAID: 'ok',
  FAILED: 'danger',
  REFUNDED: 'neutral',
};

export const INVOICE_STATUS_LABEL: Record<InvoiceStatus, string> = {
  NOT_REQUESTED: 'Sin solicitar',
  REQUESTED: 'Solicitada',
  PROCESSING: 'Procesando',
  ISSUED: 'Emitida',
  FAILED: 'Fallida',
};

export const INVOICE_STATUS_TONE: Record<InvoiceStatus, Tone> = {
  NOT_REQUESTED: 'neutral',
  REQUESTED: 'info',
  PROCESSING: 'info',
  ISSUED: 'ok',
  FAILED: 'danger',
};

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  SINPE_MOBILE: 'SINPE Móvil',
  BANK_TRANSFER: 'Transferencia bancaria',
  MANUAL_TRANSFER: 'Transferencia manual',
  ONLINE_GATEWAY: 'Pago en línea',
  APPLE_PAY: 'Apple Pay',
  CASH: 'Efectivo',
  OTHER: 'Otro',
};

export const SUBMISSION_STATUS_LABEL: Record<SubmissionStatus, string> = {
  SUBMITTED: 'Recibido',
  UNDER_REVIEW: 'En revisión',
  CONFIRMED: 'Confirmado',
  REJECTED: 'Rechazado',
};

export const SUBMISSION_STATUS_TONE: Record<SubmissionStatus, Tone> = {
  SUBMITTED: 'info',
  UNDER_REVIEW: 'warn',
  CONFIRMED: 'ok',
  REJECTED: 'danger',
};

/** Cada cuánto vence algo. La misma lista sirve para contratos y obligaciones. */
export const BILLING_FREQUENCY_LABEL: Record<BillingFrequency, string> = {
  MONTHLY: 'Mensual',
  BIMONTHLY: 'Bimensual',
  QUARTERLY: 'Trimestral',
  SEMIANNUAL: 'Semestral',
  ANNUAL: 'Anual',
  ONE_TIME: 'Una sola vez',
};

/* ── Obligaciones de la empresa (0019) ────────────────────────────────────── */

export const OBLIGATION_CATEGORY_LABEL: Record<ObligationCategory, string> = {
  TAXES: 'Impuestos',
  PAYROLL: 'Planilla',
  UTILITIES: 'Servicios',
  RENT: 'Alquiler',
  INSURANCE: 'Seguros',
  OTHER: 'Otro',
};

export const OBLIGATION_CATEGORY_TONE: Record<ObligationCategory, Tone> = {
  TAXES: 'danger',
  PAYROLL: 'info',
  UTILITIES: 'neutral',
  RENT: 'neutral',
  INSURANCE: 'warn',
  OTHER: 'neutral',
};

export const OBLIGATION_STATUS_LABEL: Record<ObligationStatus, string> = {
  PENDING: 'Pendiente',
  PAID: 'Pagada',
  CANCELLED: 'No aplica',
};

export const OBLIGATION_STATUS_TONE: Record<ObligationStatus, Tone> = {
  PENDING: 'warn',
  PAID: 'ok',
  CANCELLED: 'neutral',
};

export const TXN_DIRECTION_LABEL: Record<TxnDirection, string> = {
  INFLOW: 'Ingreso',
  OUTFLOW: 'Egreso',
};

export const REQUEST_CATEGORY_LABEL: Record<RequestCategory, string> = {
  EMERGENCY: 'Emergencia',
  ADDITIONAL_CLEANING: 'Limpieza adicional',
  DEEP_CLEANING: 'Limpieza profunda',
  SUPPLIES: 'Insumos',
  SPECIAL_REQUEST: 'Solicitud especial',
  OTHER: 'Otro',
};

export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  OPEN: 'Abierta',
  DISPATCHED: 'Despachada',
  ACKNOWLEDGED: 'Recibida',
  RESOLVED: 'Resuelta',
  CANCELLED: 'Cancelada',
  SPAM: 'Descartada',
};

export const REQUEST_STATUS_TONE: Record<RequestStatus, Tone> = {
  OPEN: 'warn',
  DISPATCHED: 'info',
  ACKNOWLEDGED: 'info',
  RESOLVED: 'ok',
  CANCELLED: 'neutral',
  SPAM: 'neutral',
};

export const INCIDENT_STATUS_LABEL: Record<IncidentStatus, string> = {
  OPEN: 'Abierta',
  IN_REVIEW: 'En revisión',
  ASSIGNED: 'Asignada',
  RESOLVED: 'Resuelta',
  CLOSED: 'Cerrada',
};

export const INCIDENT_STATUS_TONE: Record<IncidentStatus, Tone> = {
  OPEN: 'danger',
  IN_REVIEW: 'warn',
  ASSIGNED: 'info',
  RESOLVED: 'ok',
  CLOSED: 'neutral',
};

/**
 * Los estados de una entrada de horas extra. La lista canónica vive en el
 * CHECK de `overtime_entries` (migración 0008), no en `enums.ts`, así que el
 * tipo se declara acá al lado de su etiqueta: es el único lugar del código
 * que necesita nombrarlos. En femenino plural porque se leen junto a «N horas».
 */
export type OvertimeStatus = 'REPORTED' | 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED' | 'PAID';

export const OVERTIME_STATUS_LABEL: Record<OvertimeStatus, string> = {
  REPORTED: 'Reportadas',
  PENDING_APPROVAL: 'Por aprobar',
  APPROVED: 'Aprobadas',
  REJECTED: 'Rechazadas',
  PAID: 'Pagadas',
};

export const OVERTIME_STATUS_TONE: Record<OvertimeStatus, Tone> = {
  REPORTED: 'warn',
  PENDING_APPROVAL: 'warn',
  APPROVED: 'ok',
  REJECTED: 'danger',
  PAID: 'ok',
};

/**
 * Los estados de una solicitud de vacaciones. Igual que `OvertimeStatus`: la
 * lista canónica vive en el CHECK de `vacation_requests` (migración 0008), no
 * en `enums.ts`, así que el tipo se declara acá al lado de su etiqueta.
 */
export type VacationStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export const VACATION_STATUS_LABEL: Record<VacationStatus, string> = {
  REQUESTED: 'Pendiente',
  APPROVED: 'Aprobada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Cancelada',
};

export const VACATION_STATUS_TONE: Record<VacationStatus, Tone> = {
  REQUESTED: 'warn',
  APPROVED: 'ok',
  REJECTED: 'danger',
  CANCELLED: 'neutral',
};

export const INCIDENT_SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  LOW: 'Baja',
  NORMAL: 'Normal',
  HIGH: 'Alta',
};

export const VISIT_STATUS_LABEL: Record<VisitStatus, string> = {
  SCHEDULED: 'Programada',
  IN_PROGRESS: 'En curso',
  DONE: 'Realizada',
  SKIPPED: 'No realizada',
  CANCELLED: 'Cancelada',
};

export const VISIT_STATUS_TONE: Record<VisitStatus, Tone> = {
  SCHEDULED: 'info',
  IN_PROGRESS: 'warn',
  DONE: 'ok',
  SKIPPED: 'danger',
  CANCELLED: 'neutral',
};

export const VISIT_KIND_LABEL: Record<VisitKind, string> = {
  BASE: 'Ronda base',
  EXTRA: 'Visita extra',
};

/** Lo que hay que entender de cada capa, en una línea, donde se elige. */
export const VISIT_KIND_HINT: Record<VisitKind, string> = {
  BASE: 'La visita garantizada del día. Una sola por consultorio.',
  EXTRA: 'A pedido del cliente. Entra en la capacidad que sobra, nunca desplaza a la base.',
};

export const BREAK_KIND_LABEL: Record<BreakKind, string> = {
  BREAKFAST: 'Desayuno',
  LUNCH: 'Almuerzo',
  BREAK: 'Descanso',
};

/**
 * De dónde salió un cobro del histórico. Es lo que separa un dato real de uno
 * deducido, y por eso se muestra siempre que se mire el pasado.
 */
export const PROVENANCE_LABEL: Record<Provenance, string> = {
  SYSTEM: 'Generado por el sistema',
  INVOICE: 'Respaldado por factura',
  CHECKLIST: 'Registrado en el control de Excel',
  INFERRED: 'Deducido del contrato',
  UNKNOWN: 'Sin información',
};

export const PROVENANCE_TONE: Record<Provenance, Tone> = {
  SYSTEM: 'ok',
  INVOICE: 'ok',
  CHECKLIST: 'info',
  INFERRED: 'warn',
  UNKNOWN: 'danger',
};

export const PROVENANCE_HINT: Record<Provenance, string> = {
  SYSTEM: 'Lo generó Clean Blue a partir del contrato vigente.',
  INVOICE: 'Existe una factura electrónica emitida que lo respalda.',
  CHECKLIST: 'Aparece cobrado en el control de Excel, pero sin factura.',
  INFERRED: 'Se dedujo del paquete y el contrato. Nadie lo confirmó.',
  UNKNOWN: 'El mes existe pero no hay evidencia de qué pasó. Requiere revisión.',
};
